// SPDX-FileCopyrightText: 2017-2024 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { ErrorBoundary } from '@sentry/react'
import type { ReactNode } from 'react'
import React, { useCallback, useContext } from 'react'
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components'
import { Redirect } from 'wouter'

import {
  Notifications,
  NotificationsContextProvider
} from 'lib-components/Notifications'
import { EnvironmentLabel } from 'lib-components/atoms/EnvironmentLabel'
import SkipToContent from 'lib-components/atoms/buttons/SkipToContent'
import { desktopMin, zoomedMobileMax } from 'lib-components/breakpoints'
import ErrorPage from 'lib-components/molecules/ErrorPage'
import { LoginErrorModal } from 'lib-components/molecules/modals/LoginErrorModal'
import SessionExpiredModal from 'lib-components/molecules/modals/SessionExpiredModal'
import { useKeepSessionAlive } from 'lib-components/useKeepSessionAlive'
import { featureFlags } from 'lib-customizations/citizen'
import { theme } from 'lib-customizations/common'

import { useChildrenStartingNotification } from './ChildStartingNotificationHook'
import { UnwrapResult } from './async-rendering'
import {
  AuthContext,
  AuthContextProvider,
  useSessionKeepalive,
  useUser
} from './auth/state'
import { Localization, useTranslation } from './localization'
import { MessageDraftsProvider } from './messages/drafts'
import Header from './navigation/Header'
import MobileNav from './navigation/MobileNav'
import { mobileBottomNavHeight } from './navigation/const'
import GlobalDialog from './overlay/GlobalDialog'
import { OverlayContext, OverlayContextProvider } from './overlay/state'
import { InstallSuggestion } from './pwa/InstallSuggestion'
import { queryClient, QueryClientProvider } from './query'

const GlobalStyle = createGlobalStyle`
  @media screen and (max-width: ${zoomedMobileMax}) {
    html {
      overflow-x: auto;
    }
  }
`

export function App({ children }: { children: React.ReactNode }) {
  const i18n = useTranslation()

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ErrorBoundary
          fallback={() => <ErrorPage basePath="/" labels={i18n.errorPage} />}
        >
          <AuthContextProvider>
            <Localization>
              <OverlayContextProvider>
                <NotificationsContextProvider>
                  <MessageDraftsProvider>
                    <Content>{children}</Content>
                    <GlobalDialog />
                    <LoginErrorModal />
                    <div id="modal-container" />
                    <div id="datepicker-container" />
                    <div id="tooltip-container" />
                  </MessageDraftsProvider>
                </NotificationsContextProvider>
              </OverlayContextProvider>
            </Localization>
          </AuthContextProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`

const Content = React.memo(function Content({
  children
}: {
  children: React.ReactNode
}) {
  const t = useTranslation()
  const { apiVersion } = useContext(AuthContext)
  const { modalOpen } = useContext(OverlayContext)

  const { user } = useContext(AuthContext)
  const sessionKeepalive = useSessionKeepalive()
  const { sessionExpirationDetected, dismissSessionExpiredDetection } =
    useKeepSessionAlive(
      sessionKeepalive,
      user.map((usr) => !!usr).getOrElse(false)
    )
  useChildrenStartingNotification()
  return (
    <FullPageContainer>
      <SkipToContent target="main">{t.skipLinks.mainContent}</SkipToContent>
      <Header ariaHidden={modalOpen} />
      <InstallSuggestion />
      <Notifications apiVersion={apiVersion} sticky offsetTop />
      <MainContainer ariaHidden={modalOpen}>{children}</MainContainer>
      <MobileNav />
      {sessionExpirationDetected && (
        <SessionExpiredModal onClose={() => dismissSessionExpiredDetection()} />
      )}
      {!!featureFlags.environmentLabel && (
        <EnvironmentLabel>{featureFlags.environmentLabel}</EnvironmentLabel>
      )}
    </FullPageContainer>
  )
})

const MainContainer = React.memo(function MainContainer({
  ariaHidden,
  children
}: {
  ariaHidden: boolean
  children: ReactNode
}) {
  const { user } = useContext(AuthContext)
  const render = useCallback(() => <>{children}</>, [children])
  return (
    <ScrollableMain aria-hidden={ariaHidden}>
      <UnwrapResult result={user}>{render}</UnwrapResult>
    </ScrollableMain>
  )
})

export function HandleRedirection() {
  const user = useUser()

  if (!user) {
    return <Redirect replace to="/login" />
  }

  const fallbackUrl = user.authLevel !== 'STRONG' ? '/map' : '/applications'
  const hasAccessToCalendar = !!user?.accessibleFeatures.reservations
  return hasAccessToCalendar ? (
    <Redirect replace to="/calendar" />
  ) : (
    <Redirect replace to={fallbackUrl} />
  )
}

const ScrollableMain = styled.div`
  flex-grow: 1;

  padding-bottom: ${mobileBottomNavHeight}px;
  @media (min-width: ${desktopMin}) {
    padding-bottom: 0;
  }
`
